'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import { ArrowUpDownIcon, Loading03Icon } from '@terra/ui-icons';
import { Button } from '../../../components/button';
import { Field } from '../../../components/field';
import { createSelectListCollection, Select } from '../../../components/select';
import { BodyStrong, MetaDefault } from '../../../components/typography';
import { SettingsCard } from '../../settings/SettingsCard';
import { SettingsSection } from '../../settings/SettingsSection';
import { PreventionConnectionRoutingFields } from './PreventionConnectionRoutingFields';
import {
  PreventionTargetField,
  type PreventionTargetDiscovery,
} from './PreventionTargetField';
import { PreventionWafSuggestionCard } from './PreventionWafSuggestionCard';
import {
  PREVENTION_ROUTING_TARGETS,
  type PreventionRoutingTarget,
} from './prevention-routing.model';
import type { ApplicationPreventionRouting } from './useApplicationPreventionRouting';

export type ApplicationPreventionSectionProps = {
  routing: ApplicationPreventionRouting;
  discovery: PreventionTargetDiscovery;
  isConnectionsLoading: boolean;
  isSuggestionsLoading: boolean;
  isSaving: boolean;
  isClearing: boolean;
  isReadOnly?: boolean;
  onGoToIntegrations?: () => void;
};

const TARGET_LABEL_KEY = {
  [PREVENTION_ROUTING_TARGETS.CLOUDFLARE]: 'targetCloudflare',
  [PREVENTION_ROUTING_TARGETS.VERCEL]: 'targetVercel',
  [PREVENTION_ROUTING_TARGETS.AWS_WAFV2]: 'targetAwsWaf',
} as const;

const isRoutingTarget = (
  value: string | undefined,
): value is PreventionRoutingTarget =>
  Object.values(PREVENTION_ROUTING_TARGETS).some((target) => target === value);

export function ApplicationPreventionSection({
  routing,
  discovery,
  isConnectionsLoading,
  isSuggestionsLoading,
  isSaving,
  isClearing,
  isReadOnly,
  onGoToIntegrations,
}: ApplicationPreventionSectionProps) {
  const t = useTranslations('designSystem.applicationSettings.prevention');
  const { state, currentMapping, currentConnection, targetOptions } = routing;
  const targetItems = useMemo(
    () =>
      targetOptions.map((target) => ({
        value: target,
        label: t(TARGET_LABEL_KEY[target]),
      })),
    [t, targetOptions],
  );
  const targetCollection = useMemo(
    () => createSelectListCollection({ items: targetItems }),
    [targetItems],
  );
  const webAclLabels = useMemo(
    () => ({
      select: t('webAclSelectLabel'),
      placeholder: t('webAclArnPlaceholder'),
      discovering: t('webAclDiscovering'),
      noneFound: t('webAclNoneFound'),
      discoverFailed: t('webAclDiscoverFailed'),
      refresh: t('webAclRefresh'),
      confirm: t('save'),
      clear: t('clear'),
      recommended: t('webAclRecommended'),
    }),
    [t],
  );
  const handleSave = () => {
    void routing.save();
  };

  const handleClear = () => {
    void routing.clear();
  };

  const actionProps = {
    isReadOnly,
    onConfirm: handleSave,
    confirmDisabled: !state.canSave,
    confirmLoading: isSaving,
    onClear: handleClear,
    clearLoading: isClearing,
  };
  const showNotConnected =
    !routing.hasAnyRoutingOption && !isConnectionsLoading;

  return (
    <SettingsSection title={t('title')} description={t('description')}>
      {isSuggestionsLoading ? (
        <HStack gap={2} alignItems="center">
          <Loading03Icon size={14} />
          <MetaDefault color="fg.muted">{t('detectingWaf')}</MetaDefault>
        </HStack>
      ) : null}

      {routing.topSuggestion ? (
        <PreventionWafSuggestionCard
          suggestion={routing.topSuggestion}
          isReadOnly={isReadOnly}
          isApplied={routing.isSuggestionApplied}
          onUse={routing.applySuggestion}
          onHide={routing.hideSuggestion}
        />
      ) : null}

      {showNotConnected ? (
        <SettingsCard>
          <VStack gap={2} alignItems="flex-start">
            <BodyStrong>{t('notConnectedTitle')}</BodyStrong>
            <MetaDefault color="fg.muted">
              {t('notConnectedDescription')}
            </MetaDefault>
            {onGoToIntegrations ? (
              <Button variant="outline" size="sm" onClick={onGoToIntegrations}>
                {t('goToIntegrations')}
              </Button>
            ) : null}
          </VStack>
        </SettingsCard>
      ) : (
        <SettingsCard isLoading={isConnectionsLoading}>
          <VStack gap={4} alignItems="stretch">
            <MetaDefault color="fg.muted">
              {currentMapping
                ? t('mappedTo', {
                    connection:
                      currentConnection?.label ?? currentMapping.connectionId,
                  })
                : t('notMapped')}
            </MetaDefault>
            {!isReadOnly ? (
              <VStack gap={4} alignItems="stretch">
                {targetItems.length > 1 ? (
                  <Field.Root>
                    <Field.Label>{t('selectTarget')}</Field.Label>
                    <Select.Root
                      collection={targetCollection}
                      value={
                        state.effectiveTarget ? [state.effectiveTarget] : []
                      }
                      onValueChange={({ value }) => {
                        const nextTarget = value[0];

                        routing.selectTarget(
                          isRoutingTarget(nextTarget) ? nextTarget : null,
                        );
                      }}
                      positioning={{ sameWidth: true }}
                      size="sm"
                    >
                      <Select.Control>
                        <Select.Trigger>
                          <Select.ValueText placeholder={t('selectTarget')} />
                          <ArrowUpDownIcon size={14} />
                        </Select.Trigger>
                      </Select.Control>
                      <Select.Positioner>
                        <Select.Content>
                          {targetItems.map((option) => (
                            <Select.Item key={option.value} item={option}>
                              <Select.ItemText>{option.label}</Select.ItemText>
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                  </Field.Root>
                ) : null}

                {state.effectiveTarget ===
                PREVENTION_ROUTING_TARGETS.CLOUDFLARE ? (
                  <PreventionConnectionRoutingFields
                    target={PREVENTION_ROUTING_TARGETS.CLOUDFLARE}
                    connectionOptions={routing.cloudflareConnectionOptions}
                    connectionId={state.effectiveConnectionId}
                    onConnectionChange={routing.selectCloudflareConnection}
                    scopeRef={state.effectiveZoneId}
                    onScopeRefChange={routing.setZoneId}
                    discovery={discovery}
                    {...actionProps}
                  />
                ) : null}
                {state.effectiveTarget === PREVENTION_ROUTING_TARGETS.VERCEL ? (
                  <PreventionConnectionRoutingFields
                    target={PREVENTION_ROUTING_TARGETS.VERCEL}
                    connectionOptions={routing.vercelConnectionOptions}
                    connectionId={state.effectiveConnectionId}
                    onConnectionChange={routing.selectVercelConnection}
                    scopeRef={state.effectiveProjectId}
                    onScopeRefChange={routing.setProjectId}
                    discovery={discovery}
                    {...actionProps}
                  />
                ) : null}
                {state.effectiveTarget ===
                PREVENTION_ROUTING_TARGETS.AWS_WAFV2 ? (
                  <PreventionTargetField
                    discovery={discovery}
                    value={state.effectiveWebAclArn}
                    onChange={routing.setWebAclArn}
                    recommendedRef={routing.recommendedWebAclArn}
                    labels={webAclLabels}
                    {...actionProps}
                  />
                ) : null}
              </VStack>
            ) : null}
          </VStack>
        </SettingsCard>
      )}
    </SettingsSection>
  );
}
